import React, { useState } from 'react';
import { Card, Button, Alert, Spinner, Row, Col, Modal, Form } from 'react-bootstrap';

/**
 * Article Preview Component
 * Shows extracted article text (left) and HTML preview iframe (right)
 * Allows user to verify content before generation
 * Mobile: Stacked layout with extracted text first
 */
const ArticlePreview = ({
  htmlContent,
  url,
  onConfirm,
  loading,
  error,
  extractedMetadata = null,
  extractionError = null,
  creditInfo = null
}) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [showFullArticleModal, setShowFullArticleModal] = useState(false);
  const [reportNote, setReportNote] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);

  const isExtracting = !extractedMetadata && !extractionError;
  const hasContent = extractedMetadata?.content && extractedMetadata.content.length > 0;

  const handleReportProblem = async () => {
    setReportSubmitting(true);
    try {
      await fetch('/api/tools/article/report-problem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url,
          note: reportNote,
          extractedLength: extractedMetadata?.content?.length || 0
        })
      });
      setReportSubmitted(true);
      setShowReportModal(false);
    } catch (err) {
      console.error('Failed to report problem:', err);
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <>
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {/* Credit info message */}
      {creditInfo?.usedFallback && (
        <Alert variant="info" className="mb-3">
          <i className="bi bi-info-circle me-2"></i>
          {creditInfo.message}
        </Alert>
      )}

      {/* Explanatory note about full article extraction */}
      {hasContent && (
        <Alert variant="success" className="mb-3">
          <strong>Full article extracted.</strong> We extracted the complete article text from the page,
          even if parts of it are hidden behind "Read More" or "Full Story" links.
          The iframe preview on the right shows the page as it appears in a browser, but the extracted
          content on the left contains the full article.
        </Alert>
      )}

      {/* Action buttons at top */}
      <div className="d-flex gap-2 flex-wrap mb-3">
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={loading || isExtracting || !hasContent}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Processing...
            </>
          ) : (
            'This looks right - Generate Media'
          )}
        </Button>

        {!reportSubmitted && (
          <Button
            variant="outline-danger"
            onClick={() => setShowReportModal(true)}
            disabled={loading}
          >
            Report a Problem
          </Button>
        )}

        {reportSubmitted && (
          <span className="text-success align-self-center">
            Problem reported - thanks!
          </span>
        )}
      </div>

      {/* Side-by-side layout: Extracted text (left) | Iframe preview (right) */}
      <Row>
        {/* Extracted Content - Left on desktop, first on mobile */}
        <Col lg={6} className="mb-3 mb-lg-0">
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-start">
              <div>
                <strong>Extracted Article Content</strong>
                <div>
                  <small className="text-muted">
                    {isExtracting ? (
                      'Extracting content...'
                    ) : hasContent ? (
                      `${extractedMetadata.content.length.toLocaleString()} characters`
                    ) : (
                      'No content extracted'
                    )}
                  </small>
                </div>
              </div>
              {hasContent && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowFullArticleModal(true)}
                >
                  View Article Text
                </Button>
              )}
            </Card.Header>
            <Card.Body style={{ padding: 0 }}>
              {isExtracting ? (
                <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: '400px' }}>
                  <Spinner animation="border" className="mb-3" />
                  <p className="text-muted mb-1">Extracting article content...</p>
                  <small className="text-muted">This may take 30-60 seconds</small>
                </div>
              ) : extractionError ? (
                <Alert variant="warning" className="m-3">
                  <Alert.Heading style={{ fontSize: '1rem' }}>Extraction Error</Alert.Heading>
                  <p className="mb-0">{extractionError}</p>
                </Alert>
              ) : hasContent ? (
                <div style={{
                  height: '400px',
                  overflowY: 'auto',
                  padding: '1rem',
                  fontSize: '0.9rem',
                  lineHeight: '1.6'
                }}>
                  {extractedMetadata.title && (
                    <div className="mb-3">
                      <strong style={{ fontSize: '1.1rem' }}>{extractedMetadata.title}</strong>
                    </div>
                  )}
                  {extractedMetadata.byline && (
                    <div className="mb-2 text-muted" style={{ fontSize: '0.85rem' }}>
                      By {extractedMetadata.byline}
                    </div>
                  )}
                  {extractedMetadata.outlet && (
                    <div className="mb-3 text-muted" style={{ fontSize: '0.85rem' }}>
                      {extractedMetadata.outlet}
                      {extractedMetadata.published_date && (
                        <> &bull; {new Date(extractedMetadata.published_date).toLocaleDateString()}</>
                      )}
                    </div>
                  )}
                  <hr />
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {extractedMetadata.content}
                  </div>
                </div>
              ) : (
                <Alert variant="danger" className="m-3">
                  <Alert.Heading style={{ fontSize: '1rem' }}>No Content Extracted</Alert.Heading>
                  <p className="mb-0">
                    We couldn't extract article content from this page. Please report the problem
                    so we can investigate.
                  </p>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* HTML Preview - Right on desktop, second on mobile */}
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-start">
              <div>
                <strong>Page Preview</strong>
                <div><small className="text-muted">How the page appears in a browser</small></div>
              </div>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary btn-sm"
              >
                Visit Article Website <i className="bi bi-box-arrow-up-right" style={{ marginLeft: '4px' }}></i>
              </a>
            </Card.Header>
            <Card.Body style={{ padding: 0 }}>
              {htmlContent ? (
                <iframe
                  srcDoc={htmlContent}
                  title="Article Preview"
                  sandbox=""
                  style={{
                    width: '100%',
                    height: '400px',
                    border: 'none',
                    backgroundColor: '#fff',
                    borderRadius: '0 0 4px 4px'
                  }}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center" style={{ height: '400px', color: '#6c757d' }}>
                  No preview available
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>


      {/* Report Problem Modal */}
      <Modal show={showReportModal} onHide={() => setShowReportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Report a Problem</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Let us know what's wrong with the extracted content. We'll investigate and improve our system.
          </p>
          <Form.Group>
            <Form.Label>What's the issue? (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="e.g., Only extracted part of the article, wrong content extracted, etc."
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
            />
          </Form.Group>
          <div className="mt-3 p-2 bg-light rounded">
            <small className="text-muted">
              <strong>URL:</strong> {url}<br />
              <strong>Extracted:</strong> {extractedMetadata?.content?.length || 0} characters
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleReportProblem} disabled={reportSubmitting}>
            {reportSubmitting ? 'Sending...' : 'Send Report'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Full Article Modal */}
      <Modal
        show={showFullArticleModal}
        onHide={() => setShowFullArticleModal(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {extractedMetadata?.title || 'Full Article'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {extractedMetadata?.byline && (
            <p className="text-muted mb-1" style={{ fontSize: '0.9rem' }}>
              By {extractedMetadata.byline}
            </p>
          )}
          {extractedMetadata?.outlet && (
            <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
              {extractedMetadata.outlet}
              {extractedMetadata.published_date && (
                <> &bull; {new Date(extractedMetadata.published_date).toLocaleDateString()}</>
              )}
            </p>
          )}
          <hr />
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>
            {extractedMetadata?.content}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <small className="text-muted me-auto">
            {extractedMetadata?.content?.length?.toLocaleString()} characters
          </small>
          <Button variant="secondary" onClick={() => setShowFullArticleModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ArticlePreview;
